import { NetworkResponse } from "~/app/type/common";
import { requestREST } from "~/client/http/CommonHTTP";
import {
  EmployeesReportIProps,
  IIvideonGetTokenResponse,
  IIvideonTokenCreateParams,
  IIvideonTokenCreateResponse,
  WorkOrderReportIProps,
} from "~/entity/Ivideon";
import {
  AcceptActIProps,
  AcceptCameraIProps,
  ActionResponseIProps,
  AnalyticsReportIProps,
  CameraStatusIProps,
  createTariffIProps,
  DeacceptActIProps,
  DealerResponse,
  DetectedType,
  fileHashIProps,
  IdTariffIProps,
  IvideonAnalyticsIProps,
  IvideonDealerDocsIProps,
  IvideonFaceAnalytics,
  IvideonHistoryActsIProps,
  IvideonHistoryActsResponseIProps,
  IvideonHistoryCamerasIProps,
  IvideonHistoryIProps,
  IvideonHistoryResponseIProps,
  IvideonSubscriptiionsIProps,
  ivideonSubscriptionsListIProps,
  IvideonTariffsIProps,
  IvideonVehicleAnalytics,
  SendActionIProps,
} from "~/entity/Ivideon/model/schema";

const ivideonServiceMap: Record<string, string> = {
  createToken: "/ivideon/create-token",
  getToken: "/ivideon/get-token",
  synchDealer: "/ivideon/synchDealer",
  sendAction: "/ivideon/log-action",
  ivideonDocs: "/ivideon/dealer/docs",
  ivideonDealers: "/ivideon/dealers",
  ivideonLicensesReport: "/ivideon/licenses_report",
  acceptAct: "/ivideon/act/accept",
  deacceptAct: "/ivideon/groups/deaccept",
  acceptCamera: "/ivideon/cameras/accept",
  ivideonHistory: "/ivideon/history",
  ivideonHistoryCameras: "/ivideon/history/cameras",
  ivideonHistoryActs: "/ivideon/history/acts",
  tariffs: "/ivideon/cameraTariffs",
  cameraAvailabilityReport: "/ivideon/camera-availability-report",
  getDealers: "/ivideon/dealers",
  workOrderReport: "/ivideon/work-order-report",
  employeesReport: "/ivideon/employees-report",
  ivideonReport: "/ivideon/report",
  dealerOfflineReport: "/ivideon/dealer-offline-report",
  analyticsFace: "/ivideon/face-analytics",
  analyticsVehicle: "/ivideon/vehicle-analytics",
  subscriptions: "/ivideon/dealers",
  subscriptionsList: "/ivideon/dealer-subscriptions",
  faceAnalyticsTodayReport: "/ivideon/face-analytics-today-report",
  faceAnalyticsWeekReport: "/ivideon/face-analytics-week-report",
  faceAnalyticsMonthReport: "/ivideon/face-analytics-month-report",
  faceAnalyticsReport: "/ivideon/face-analytics-report",
  carsAnalyticsTodayReport: "/ivideon/vehicle-analytics-today-report",
  carsAnalyticsWeekReport: "/ivideon/vehicle-analytics-week-report",
  carsAnalyticsMonthReport: "/ivideon/vehicle-analytics-month-report",
  carsAnalyticsReport: "/ivideon/vehicle-analytics-report",
  cameraCheckStatus: "/ivideon/status/check",
};

export class IvideonService {
  static GET = {
    getToken: (
      dealerId: string,
      force = false,
    ): NetworkResponse<IIvideonGetTokenResponse> =>
      requestREST.get(ivideonServiceMap.getToken, {
        params: { dealerId, force },
      }),
    synchGroupDealer: (dealerId: string): NetworkResponse<boolean> =>
      requestREST.get(ivideonServiceMap.synchDealer, { params: { dealerId } }),
    getIvideonDealerDocs: (
      params: string,
    ): NetworkResponse<IvideonDealerDocsIProps> =>
      requestREST.get(ivideonServiceMap.ivideonDocs + "?" + params),
    getIvideonDealers: (params: string): NetworkResponse<DealerResponse> =>
      requestREST.get(ivideonServiceMap.ivideonDealers + "?" + params),
    getIvideonLicensesReport: () =>
      requestREST.get(ivideonServiceMap.ivideonLicensesReport),
    getIvideonHistory: (
      params: IvideonHistoryIProps,
    ): Promise<IvideonHistoryResponseIProps> =>
      requestREST
        .get(ivideonServiceMap.ivideonHistory, { params })
        .then((response) => response.data),
    getIvideonHistoryCameras: (
      params: IvideonHistoryCamerasIProps,
    ): Promise<IvideonHistoryResponseIProps> =>
      requestREST
        .get(ivideonServiceMap.ivideonHistoryCameras, { params })
        .then((response) => response.data),
    getIvideonHistoryActs: (
      params: IvideonHistoryActsIProps,
    ): Promise<IvideonHistoryActsResponseIProps> =>
      requestREST
        .get(ivideonServiceMap.ivideonHistoryActs, { params })
        .then((response) => response.data),
    getTariffs: (): NetworkResponse<IvideonTariffsIProps[] | []> =>
      requestREST.get(ivideonServiceMap.tariffs),
    getAvailabilityReport: (params: string): NetworkResponse<any> =>
      requestREST.get(
        `${ivideonServiceMap.cameraAvailabilityReport}?${params}`,
      ),
    getDealersIvideon: (params: string): NetworkResponse<any> =>
      requestREST.get(`${ivideonServiceMap.getDealers}?${params}`),
    getIvideonReport: (params: string): NetworkResponse<fileHashIProps> =>
      requestREST.get(`${ivideonServiceMap.ivideonReport}?${params}`),
    getDealerOfflineReport: (params: string): NetworkResponse<fileHashIProps> =>
      requestREST.get(`${ivideonServiceMap.dealerOfflineReport}?${params}`),
    getSubscriptions: (
      count: number,
    ): NetworkResponse<IvideonSubscriptiionsIProps> =>
      requestREST.get(`${ivideonServiceMap.subscriptions}?count=${count}`),
    getSubscriptionsList: (
      params?: DetectedType,
    ): NetworkResponse<ivideonSubscriptionsListIProps> =>
      requestREST.get(ivideonServiceMap.subscriptionsList, { params }),
    cameraCheckStatus: (
      dealerId: string,
    ): NetworkResponse<CameraStatusIProps> =>
      requestREST.get(`${ivideonServiceMap.cameraCheckStatus}/${dealerId}`),
  };

  static POST = {
    createToken: (
      data: IIvideonTokenCreateParams,
    ): NetworkResponse<IIvideonTokenCreateResponse> =>
      requestREST.post(ivideonServiceMap.createToken, data),
    sendAction: (
      data: SendActionIProps,
    ): NetworkResponse<ActionResponseIProps> =>
      requestREST.post(ivideonServiceMap.sendAction, data),
    createTariff: (
      data: createTariffIProps,
    ): NetworkResponse<IvideonTariffsIProps[]> =>
      requestREST.post(ivideonServiceMap.tariffs, data),
    getWorkOrderReport: (
      data: WorkOrderReportIProps,
    ): NetworkResponse<fileHashIProps> =>
      requestREST.post(ivideonServiceMap.workOrderReport, data),
    getEmployeesReport: (
      data: EmployeesReportIProps,
    ): NetworkResponse<fileHashIProps> =>
      requestREST.post(ivideonServiceMap.employeesReport, data),
    getAnalyticsFace: (
      data: IvideonAnalyticsIProps,
    ): NetworkResponse<IvideonFaceAnalytics> =>
      requestREST.post(ivideonServiceMap.analyticsFace, data),
    getAnalyticsVehicle: (
      data: IvideonAnalyticsIProps,
    ): NetworkResponse<IvideonVehicleAnalytics> =>
      requestREST.post(ivideonServiceMap.analyticsVehicle, data),
    getAnalyticaReport: (
      apiKey: string,
      data: AnalyticsReportIProps,
    ): NetworkResponse<fileHashIProps> =>
      requestREST.post(ivideonServiceMap[apiKey], data),
  };
  static PUT = {
    acceptAct: (data: AcceptActIProps) =>
      requestREST.put(ivideonServiceMap.acceptAct, data),
    deacceptAct: (data: DeacceptActIProps) =>
      requestREST.put(ivideonServiceMap.deacceptAct, data),
    acceptCamera: (data: AcceptCameraIProps) =>
      requestREST.put(ivideonServiceMap.acceptCamera, data),
    updateTariff: (
      data: IvideonTariffsIProps,
    ): NetworkResponse<IvideonTariffsIProps> =>
      requestREST.put(ivideonServiceMap.tariffs, data),
  };
  static DELETE = {
    deleteTariff: (data: IdTariffIProps): NetworkResponse<IdTariffIProps> =>
      requestREST.delete(ivideonServiceMap.tariffs, { data }),
  };
}
